import React from "react";
import { Calendar, Zap, Sparkles, Building2 } from "lucide-react";
import Button from "../ui/Button";

const STATUS_BADGE = {
  trialing:   { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-500",    label: "Trialing" },
  active:     { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "Active" },
  past_due:   { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     label: "Past due" },
  canceled:   { bg: "bg-muted",   text: "text-muted-foreground",    dot: "bg-gray-400",    label: "Canceled" },
  unpaid:     { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     label: "Unpaid" },
  incomplete: { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500",   label: "Incomplete" },
};

// Who scheduled a pending change, when the backend tells us — either the
// per-plan requester on `pending_plan` (a scheduled downgrade/upgrade) or the
// bare `pending_change_requested_by` (cancel-at-period-end with no plan
// change). Nothing renders when neither is present.
function ScheduledBy({ requestedBy, requestedAt }) {
  if (!requestedBy) return null;
  const who = requestedBy.name || requestedBy.email;
  return (
    <div className="text-xs text-muted-foreground mt-1">
      Scheduled by {who}{requestedAt ? ` on ${new Date(requestedAt).toLocaleDateString()}` : ""}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { bg: "bg-muted", text: "text-muted-foreground", dot: "bg-gray-400", label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// Per-plan accent — subtle gradient + icon
const PLAN_THEME = {
  starter: {
    gradient: "from-slate-50 via-white to-slate-50",
    ring: "ring-slate-100",
    iconBg: "bg-slate-100",
    iconColor: "text-slate-600",
    Icon: Zap,
  },
  growth: {
    gradient: "from-indigo-50 via-white to-purple-50",
    ring: "ring-indigo-100",
    iconBg: "bg-gradient-to-br from-indigo-500 to-purple-500",
    iconColor: "text-white",
    Icon: Sparkles,
  },
  enterprise: {
    gradient: "from-amber-50 via-white to-orange-50",
    ring: "ring-amber-100",
    iconBg: "bg-gradient-to-br from-amber-400 to-orange-500",
    iconColor: "text-white",
    Icon: Building2,
  },
  pay_as_you_go: {
    gradient: "from-slate-50 via-white to-slate-50",
    ring: "ring-slate-100",
    iconBg: "bg-slate-100",
    iconColor: "text-slate-600",
    Icon: Zap,
  },
};

export default function CurrentPlanCard({ subscription: s, onUpgrade, onReactivate }) {
  if (!s || s.status === "none") {
    return (
      <div className="rounded-2xl border border-border shadow-sm bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
          <Zap className="h-4 w-4 text-muted-foreground" />
          Plan
        </div>
        <p className="text-sm text-muted-foreground mb-4">No active subscription.</p>
        <Button variant="primary" onClick={onUpgrade}>
          <Zap className="h-4 w-4" />
          <span>Choose a plan</span>
        </Button>
      </div>
    );
  }

  if (s.plan?.code === "pay_as_you_go") {
    return (
      <div className="rounded-2xl border border-border shadow-sm bg-gradient-to-br from-slate-50 via-white to-slate-50 ring-1 ring-slate-100 p-4 h-full flex flex-col">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1">
          <Zap className="h-4 w-4 text-muted-foreground" /> Pay as you go
        </div>
        <div className="text-2xl font-semibold text-foreground">$0<span className="text-sm font-normal text-muted-foreground"> /month</span></div>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Billed only for what you run. Credits you buy never expire.</p>
        {s.pending_plan && s.pending_plan.code !== "pay_as_you_go" && (
          <div className="mb-3">
            <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
              Upgrading to {s.pending_plan.name} on {new Date(s.pending_plan.effective_at).toLocaleDateString()}
            </div>
            <ScheduledBy requestedBy={s.pending_plan.requested_by} requestedAt={s.pending_plan.requested_at} />
          </div>
        )}
        <div className="mt-auto">
          <Button variant="primary" onClick={onUpgrade}>
            <Sparkles className="h-4 w-4" /><span>Upgrade to Growth</span>
          </Button>
        </div>
      </div>
    );
  }

  const isEnterprise = s.plan && s.plan.is_public === false;
  const theme = PLAN_THEME[s.plan?.code] || PLAN_THEME.starter;
  const { Icon } = theme;
  // Two very different states that both read as "canceled" to a user, and only ONE of
  // them is reversible:
  //
  //   cancel_at_period_end  — a PENDING cancellation. The Stripe subscription is still
  //                           alive; flipping the flag undoes it. Reactivate works.
  //   status === canceled   — TERMINAL. Stripe has ended the subscription and rejects
  //                           any update to it ("A canceled subscription can only update
  //                           its cancellation_details"). It cannot be revived; the user
  //                           has to subscribe again through Checkout.
  //
  // Treating them alike offered a Reactivate button that could only ever fail.
  // A pending downgrade to pay-as-you-go sets cancel_at_period_end (the paid Stripe
  // subscription IS ending) alongside pending_plan. That combination must read as a
  // downgrade, not a bare cancellation — otherwise it looks like the user is losing
  // service entirely instead of moving to a $0 plan.
  const isPendingCancellation =
    Boolean(s.cancel_at_period_end) && s.status !== "canceled" && s.pending_plan?.code !== "pay_as_you_go";
  const isTerminallyCanceled = s.status === "canceled";
  // A scheduled downgrade is a Stripe SubscriptionSchedule — it sets neither
  // cancel_at_period_end nor status: canceled, so it matches neither state above and
  // needs its own branch. Undoing it is the same call as reactivate: the backend
  // releases the schedule and clears pending_plan_id / pending_change_at.
  const hasPendingDowngrade = Boolean(s.pending_plan);

  return (
    <div
      className={`relative rounded-2xl border border-border shadow-sm overflow-hidden bg-gradient-to-br ${theme.gradient} ring-1 ${theme.ring} h-full flex flex-col`}
    >
      {/* Status pill — floats top-right, no header bar */}
      <div className="absolute top-4 right-4">
        <StatusBadge status={s.status} />
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 rounded-xl ${theme.iconBg} shadow-sm flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-5 w-5 ${theme.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-semibold text-foreground leading-tight">
              {s.plan?.name}
            </div>
            {s.plan?.monthly_price_cents != null ? (
              <div className="text-sm text-muted-foreground mt-0.5">
                <span className="text-foreground font-medium">${(s.plan.monthly_price_cents / 100).toFixed(0)}</span>
                <span className="text-muted-foreground"> /month</span>
              </div>
            ) : isEnterprise ? (
              <div className="text-sm text-muted-foreground mt-0.5">Custom pricing</div>
            ) : null}
          </div>
        </div>

        {/* Active / past_due period details */}
        {s.current_period_end && s.status !== "trialing" && (
          <div className="mb-4">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              {s.pending_plan?.code === "pay_as_you_go"
                ? "Switches to Pay as you go on"
                : s.cancel_at_period_end
                ? "Canceling on"
                : "Renews on"}{" "}
              {new Date(s.current_period_end).toLocaleDateString()}
            </div>
            {/* Plain pending cancellation (no plan change) — the only other
                spot the backend's pending_change_requested_by applies to.
                Gated on cancel_at_period_end (not just "no pending_plan"):
                those top-level requested_by/at columns can go stale if a
                cancellation is undone straight from the Stripe
                portal/dashboard rather than through our reactivate
                endpoint, so they must never be shown once
                cancel_at_period_end has flipped back to false. */}
            {Boolean(s.cancel_at_period_end) && !s.pending_plan && (
              <ScheduledBy
                requestedBy={s.pending_change_requested_by}
                requestedAt={s.pending_change_requested_at}
              />
            )}
          </div>
        )}

        {/* Pending downgrade */}
        {s.pending_plan && (
          <div className="mb-4">
            <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
              Downgrading to {s.pending_plan.name} on {new Date(s.pending_plan.effective_at).toLocaleDateString()}
            </div>
            <ScheduledBy
              requestedBy={s.pending_plan.requested_by || s.pending_change_requested_by}
              requestedAt={s.pending_plan.requested_at || s.pending_change_requested_at}
            />
          </div>
        )}

        {/* Primary action — pinned to bottom */}
        <div className="mt-auto pt-4">
          {isEnterprise ? (
            <p className="text-sm text-muted-foreground">
              Custom Enterprise plan. Contact your account manager for changes.
            </p>
          ) : isPendingCancellation ? (
            // Reversible: the Stripe subscription is alive, just flagged to end.
            <Button variant="primary" onClick={onReactivate}>
              <span>Cancel pending cancellation</span>
            </Button>
          ) : isTerminallyCanceled ? (
            // NOT reversible. Reactivate would call Stripe::Subscription.update on a dead
            // subscription and fail every time — send them to Checkout for a new one.
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={onUpgrade}>
                <span>Subscribe again</span>
              </Button>
              <Button variant="secondary" onClick={onUpgrade}>
                <span>Choose a plan</span>
              </Button>
            </div>
          ) : hasPendingDowngrade ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={onReactivate}>
                <span>Keep {s.plan?.name}</span>
              </Button>
              <Button variant="secondary" onClick={onUpgrade}>
                <span>Change plan</span>
              </Button>
            </div>
          ) : (
            <Button variant="primary" onClick={onUpgrade}>
              <span>Change plan</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
